/**
 * ============================================
 * DASHBOARD COMPONENT
 * ============================================
 * 
 * Layout:
 *   TOP: Monitor/Analytics toggle + Environment tabs + status panel
 *   BOTTOM: SSL & Domains (Monitor mode only)
 * 
 * Two modes:
 *   - Monitor: Server monitoring dashboard (default)
 *   - Analytics: Game analytics dashboard
 * 
 * Player stats are now environment-specific (inside EnvironmentPanel)
 * Default: Production (live) environment
 * 
 * Environment visibility is controlled by settings - disabled environments
 * won't appear in the tabs.
 */

import { useState, useEffect, useCallback } from 'react';
import { Code2, FlaskConical, Rocket, Activity, BarChart3, Sun, Moon, Gamepad2, Building2, ShieldOff, Swords } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { appMetadata } from '@/config';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { useEnvironments, useAlertPolling } from '@/hooks';
import { getEnvironmentAll } from '@/services/api';
import { getAlertSettings } from '@/utils/notifications';
import { getEffectiveRole } from '@/utils/roles';
import { EnvironmentPanel } from './EnvironmentPanel';
import { CommonSection } from './CommonSection';
import { DashboardHeader } from './DashboardHeader';
import { RemoteConfigModal } from './RemoteConfigModal';
import { MultiOrgPreview } from './MultiOrgPreview';

const PREVIEW_MULTI_ORG = import.meta.env.VITE_PREVIEW_MULTI_ORG === 'true';
import { AlertHistory, CriticalAlarmModal, SettingsModal } from '@/components/alerts';
import { AnalyticsDashboard } from '@/components/analytics';
import { PS5Dashboard } from '@/components/ps5';
import { FriendCodeDashboard } from '@/components/friendcode';
import { MultiplayerDashboard } from '@/components/multiplayer';
import { DateRangeProvider } from '@/context/DateRangeContext';

// Dashboard mode type
type DashboardMode = 'monitor' | 'analytics' | 'ps5' | 'multiplayer' | 'friendcode';

// Icons for each environment
const environmentIcons: Record<string, React.ReactNode> = {
  dev: <Code2 className="h-6 w-6" />,
  stage: <FlaskConical className="h-6 w-6" />,
  live: <Rocket className="h-6 w-6" />,
};

// Mode Toggle Component
// `isMonitor` hides Analytics + Sony/DOMO + Multiplayer buttons -- monitor role is
// restricted to the Monitor tab only. `hideMonitor` hides the Monitor button --
// the external role sees the analytics tabs only.
function ModeToggle({
  mode,
  onChange,
  isMonitor = false,
  hideMonitor = false,
}: {
  mode: DashboardMode;
  onChange: (mode: DashboardMode) => void;
  isMonitor?: boolean;
  hideMonitor?: boolean;
}) {
  return (
    <div className="flex items-center bg-muted/50 rounded-lg p-1">
      {!hideMonitor && (
      <button
        onClick={() => onChange('monitor')}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          mode === 'monitor'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <Activity className="h-4 w-4" />
        Monitor
      </button>
      )}
      {!isMonitor && (
        <>
          <button
            onClick={() => onChange('analytics')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === 'analytics'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Analytics
          </button>
          <button
            onClick={() => onChange('ps5')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === 'ps5'
                ? 'bg-background text-blue-600 shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Gamepad2 className={`h-4 w-4 ${mode === 'ps5' ? 'text-blue-600' : ''}`} />
            Sony/DOMO
          </button>
          <button
            onClick={() => onChange('multiplayer')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              mode === 'multiplayer'
                ? 'bg-background text-purple-600 shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Swords className={`h-4 w-4 ${mode === 'multiplayer' ? 'text-purple-600' : ''}`} />
            Multiplayer
          </button>
        </>
      )}
    </div>
  );
}

// Theme Toggle Component (Light/Dark mode)
function ThemeToggle() {
  const { isDarkMode, toggleTheme } = useTheme();
  
  return (
    <div className="flex items-center gap-2">
      <Sun className={`h-4 w-4 transition-colors ${isDarkMode ? 'text-muted-foreground' : 'text-yellow-500'}`} />
      <button
        onClick={toggleTheme}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          isDarkMode ? 'bg-primary' : 'bg-muted'
        }`}
        role="switch"
        aria-checked={isDarkMode}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
            isDarkMode ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
      <Moon className={`h-4 w-4 transition-colors ${isDarkMode ? 'text-primary' : 'text-muted-foreground'}`} />
    </div>
  );
}

export function Dashboard() {
  const { user, logout, isMonitorApp } = useAuth();
  const { environments, isLoading: envsLoading, error: envsError } = useEnvironments();
  const [showAlertHistory, setShowAlertHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRemoteConfig, setShowRemoteConfig] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('live');
  const [, setEnabledEnvironments] = useState<Record<string, boolean>>({});
  const [dashboardMode, setDashboardMode] = useState<DashboardMode>('monitor');
  const [awsOrgName, setAwsOrgName] = useState<string>('');
  const [isExportingCSV, setIsExportingCSV] = useState(false);

  // Resolve a single effective role from the user's Cognito groups via
  // fixed precedence (admin > external > monitor). See utils/roles.ts.
  // null = user has no recognised role -> we render the no-access screen.
  const effectiveRole = getEffectiveRole(user?.groups);
  const isAdmin = effectiveRole === 'admin';
  const isExternal = effectiveRole === 'external';
  const isMonitor = effectiveRole === 'monitor';

  // Effective dashboard mode -- monitor users are locked to 'monitor' even
  // if some stale state somehow set dashboardMode to 'analytics' etc. External
  // users never see Monitor: they land on Analytics instead.
  const effectiveMode: DashboardMode = isMonitor
    ? 'monitor'
    : isExternal && dashboardMode === 'monitor' ? 'analytics' : dashboardMode;

  // Poll for alerts from backend.
  // Disabled when: external user (silent viewer by design) OR no recognised
  // role at all (we render the no-access screen below, but the hook runs
  // unconditionally above the early return, so gate it here too).
  useAlertPolling(isExternal || !effectiveRole);

  // Load enabled environments from settings
  useEffect(() => {
    const settings = getAlertSettings();
    setEnabledEnvironments(settings.enabledEnvironments);
  }, []);

  // Show ALL environments in tabs — alert settings only control alarms, not visibility
  const visibleEnvironments = environments;

  // Set default environment once loaded
  useEffect(() => {
    if (visibleEnvironments.length > 0) {
      // If current selection is not in list, switch to first
      if (!visibleEnvironments.find(env => env.id === selectedEnvironment)) {
        const liveEnv = visibleEnvironments.find(env => env.id === 'live');
        setSelectedEnvironment(liveEnv?.id || visibleEnvironments[0]?.id || 'live');
      }
    }
  }, [visibleEnvironments, selectedEnvironment]);

  // CSV Export: fetch env aggregates on-demand. Previous implementation
  // mounted a hidden helper per env at app load that polled /environments/*/all
  // every 30s; those eager fetches saturated the backend and stalled the
  // visible panel's own fetch until the user tab-hopped.
  const handleExportCSV = useCallback(async () => {
    if (isExportingCSV) return; // guard against double-click
    setIsExportingCSV(true);
    try {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const timeStr = now.toTimeString().slice(0, 5).replace(':', ''); // HHMM
      const dateTimeKey = `${dateStr}_${timeStr}`;

      // Platform IDs: 0 = PC, 1 = Mac, 2 = Linux, 3 = iOS, 4 = Android, 5 = PlayStation, 6 = Xbox, 7 = Switch
      let pcCount = 0;
      let playstationCount = 0;
      let iosCount = 0;
      let crossPlatformCount = 0;
      let s3Objects = 0;

      const results = await Promise.all(
        visibleEnvironments.map(async env => {
          try {
            return await getEnvironmentAll(env.id);
          } catch (err) {
            console.error(`CSV export: failed to fetch ${env.id}`, err);
            return null;
          }
        })
      );

      results.forEach(data => {
        if (!data) return;
        const breakdown = data.players?.platform_breakdown || {};
        pcCount += breakdown['0'] || 0;
        playstationCount += breakdown['5'] || 0;
        iosCount += breakdown['3'] || 0;
        Object.values(breakdown).forEach(count => {
          crossPlatformCount += count;
        });
        s3Objects += data.infrastructure?.s3_objects || 0;
      });

      const headers = ['DateTime', 'PC', 'PlayStation', 'iOS', 'CrossPlatform', 'LevelSaves'];
      const row = [dateTimeKey, pcCount, playstationCount, iosCount, crossPlatformCount, s3Objects];

      const csvContent = [
        headers.join(','),
        row.join(','),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `lokko_stats_${dateTimeKey}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setIsExportingCSV(false);
    }
  }, [visibleEnvironments, isExportingCSV]);

  // Handle environment settings change (only affects alarm behavior, not tab visibility)
  const handleEnvironmentsChange = (newEnabledEnvs: Record<string, boolean>) => {
    setEnabledEnvironments(newEnabledEnvs);
  };

  // No-access gate: a user with NO recognised role (no Cognito group, or
  // only unknown groups) sees nothing -- not the env list, not the
  // monitoring UI, not even the data fetches kick off below. Forces an
  // admin to assign a role before they get any visibility.
  if (!effectiveRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto p-6 rounded-2xl border border-border bg-card">
          <ShieldOff className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold mb-2">No access</h2>
          <p className="text-muted-foreground mb-4">
            Your account <code className="text-foreground">{user?.username}</code> is signed
            in but has not been assigned a role. Ask an administrator to add you to one
            of the Cognito groups (<code>admin</code>, <code>external</code>,
            or <code>monitor</code>).
          </p>
          <button
            onClick={logout}
            className="px-4 py-2 rounded-lg border border-border hover:bg-muted text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Environments only feed the Monitor view, which external users never see,
  // so an environments failure must not block their analytics tabs.
  if (envsLoading && !isExternal) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading environments...</p>
        </div>
      </div>
    );
  }

  if (!isExternal && (envsError || environments.length === 0)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold mb-2">Failed to Load</h2>
          <p className="text-muted-foreground mb-4">
            {envsError || 'No environments available. Is the backend running?'}
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Backend URL: {import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000'}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DateRangeProvider>
    <div className="min-h-screen bg-background">
      <DashboardHeader
        username={user?.username || 'User'}
        userGroups={user?.groups}
        isExternal={isExternal}
        isMonitor={isMonitor}
        isMonitorApp={isMonitorApp}
        onLogout={logout}
        appName={appMetadata.name}
        onShowAlertHistory={() => setShowAlertHistory(true)}
        onShowSettings={() => setShowSettings(true)}
        onShowRemoteConfig={() => setShowRemoteConfig(true)}
        onExportCSV={handleExportCSV}
        modeToggle={<ModeToggle mode={effectiveMode} onChange={setDashboardMode} isMonitor={isMonitor} hideMonitor={isExternal} />}
        themeToggle={<ThemeToggle />}
      />

      {/* Render based on effective mode (monitor role locked to 'monitor') */}
      {effectiveMode === 'friendcode' ? (
        <main className="container mx-auto">
          <FriendCodeDashboard />
        </main>
      ) : effectiveMode === 'ps5' ? (
        <main className="container mx-auto">
          <PS5Dashboard />
        </main>
      ) : effectiveMode === 'multiplayer' ? (
        <main className="container mx-auto">
          <MultiplayerDashboard isAdmin={isAdmin} />
        </main>
      ) : effectiveMode === 'analytics' ? (
        <main className="container mx-auto">
          <AnalyticsDashboard />
        </main>
      ) : (
        <main className="container mx-auto px-4 py-6 space-y-6">
          {/* Multi-org failover engine view: controls + dual-org status panels.
              Renders ABOVE the per-env content. Toggled by VITE_PREVIEW_MULTI_ORG. */}
          {PREVIEW_MULTI_ORG && <MultiOrgPreview />}

          {PREVIEW_MULTI_ORG ? (
            // When multi-org failover is on, the dual-org panels above already
            // show service health for every env in the priority chain. Down here
            // we only need the LIVE env's deep view (CCU graph + platform
            // breakdown + user growth chart) -- those aren't covered above.
            // Monitor role skips the deep view: they only need failover status.
            !isMonitor && (() => {
              const liveEnv = visibleEnvironments.find(env => env.id === 'live');
              return liveEnv
                ? <EnvironmentPanel environment={liveEnv} onOrgName={setAwsOrgName} />
                : null;
            })()
          ) : (
            // Legacy single-org view: original env tabs + per-env panel
            <Tabs
              value={selectedEnvironment}
              onValueChange={setSelectedEnvironment}
              className="w-full"
            >
              <div className="flex flex-col items-center gap-1 mb-6">
                {awsOrgName && (
                  <div className="flex items-center gap-1.5 text-muted-foreground/40">
                    <Building2 className="h-3.5 w-3.5" />
                    <span className="text-xs font-medium tracking-widest uppercase">{awsOrgName}</span>
                  </div>
                )}
                <TabsList className="bg-muted/50 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl h-auto flex-wrap justify-center">
                  {visibleEnvironments.map((env) => (
                    <TabsTrigger
                      key={env.id}
                      value={env.id}
                      className="flex items-center gap-1.5 sm:gap-3 px-4 sm:px-8 py-2.5 sm:py-4 text-sm sm:text-lg font-semibold data-[state=active]:bg-background rounded-lg sm:rounded-xl"
                    >
                      <span className="h-4 w-4 sm:h-6 sm:w-6 [&>svg]:h-4 [&>svg]:w-4 sm:[&>svg]:h-6 sm:[&>svg]:w-6">
                        {environmentIcons[env.id] || <Code2 className="h-6 w-6" />}
                      </span>
                      {env.shortName}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              {visibleEnvironments.map((env) => (
                <TabsContent key={env.id} value={env.id}>
                  <EnvironmentPanel environment={env} onOrgName={setAwsOrgName} />
                </TabsContent>
              ))}
            </Tabs>
          )}

          {/* SSL & Domains (common section) -- hidden for monitor role */}
          {!isMonitor && <CommonSection />}
        </main>
      )}

      <AlertHistory
        isOpen={showAlertHistory}
        onClose={() => setShowAlertHistory(false)}
      />

      <RemoteConfigModal
        isOpen={showRemoteConfig}
        onClose={() => setShowRemoteConfig(false)}
        isAdmin={isAdmin}
        username={user?.username || 'Unknown'}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onEnvironmentsChange={handleEnvironmentsChange}
      />

      <CriticalAlarmModal />

      {isExportingCSV && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/80 backdrop-blur-sm"
          role="alertdialog"
          aria-busy="true"
          aria-label="Exporting CSV"
        >
          <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-8 shadow-2xl">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground">Exporting CSV…</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Fetching stats for all environments. Do not close this window.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                To cancel, reload the page.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
    </DateRangeProvider>
  );
}
